import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserStateService } from './user-state.service';

export interface NavigationItem {
  title: string;
  icon?: string;
  link?: string;
  children?: NavigationItem[];
  adminOnly?: boolean;
  /** Org contributors (jobs) can access */
  orgUserAccess?: boolean;
  /** Manufacturer users (adverts) can access */
  manufacturerAccess?: boolean;
  hidden?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private userState = inject(UserStateService);
  
  private defaultNavItems: NavigationItem[] = [
    {
      title: 'Dashboard',
      icon: 'dashboard',
      link: '/dashboard',
      orgUserAccess: true,
      manufacturerAccess: true
    },
    {
      title: 'Content',
      icon: 'article',
      link: '/content',
      adminOnly: true
    },
    {
      title: 'Job Postings',
      icon: 'work',
      link: '/jobs',
      orgUserAccess: true
    },
    {
      title: 'Adverts',
      icon: 'campaign',
      link: '/adverts',
      manufacturerAccess: true,
      adminOnly: true
    },
    {
      title: 'Manufacturers',
      icon: 'business',
      link: '/manufacturers',
      adminOnly: true
    },
    {
      title: 'Product Categories',
      icon: 'category',
      link: '/product-categories',
      adminOnly: true
    },
    {
      title: 'Industries',
      icon: 'apartment',
      link: '/industries',
      adminOnly: true
    },
    {
      title: 'Organisations',
      icon: 'business',
      link: '/organisations',
      adminOnly: true
    },
    {
      title: 'Users',
      icon: 'people',
      link: '/users',
      adminOnly: true
    }
  ];
  
  private navItemsSubject = new BehaviorSubject<NavigationItem[]>(this.defaultNavItems);
  
  readonly navItems$: Observable<NavigationItem[]> = combineLatest([
    this.navItemsSubject,
    this.userState.isAdmin$,
    this.userState.organisationId$,
    this.userState.manufacturerId$
  ]).pipe(
    map(([items, isAdmin, organisationId, manufacturerId]) => {
      return this.filterNavItemsByRole(
        items,
        isAdmin,
        !!organisationId,
        !!manufacturerId
      );
    })
  );
  
  private filterNavItemsByRole(
    items: NavigationItem[],
    isAdmin: boolean,
    isOrgUser: boolean,
    isManufacturerUser: boolean
  ): NavigationItem[] {
    return items
      .filter(item => {
        if (isAdmin) return true;
        if (isOrgUser && item.orgUserAccess === true) return true;
        if (isManufacturerUser && item.manufacturerAccess === true) return true;
        if (!isOrgUser && !isManufacturerUser) return !item.adminOnly;
        return false;
      })
      .map(item => {
        if (item.children) {
          const filteredChildren = this.filterNavItemsByRole(
            item.children,
            isAdmin,
            isOrgUser,
            isManufacturerUser
          );
          
          if (filteredChildren.length === 0) {
            return { ...item, hidden: true };
          }
          
          return { ...item, children: filteredChildren };
        }
        return item;
      })
      .filter(item => !item.hidden);
  }
  
  updateNavItems(items: NavigationItem[]): void {
    this.navItemsSubject.next(items);
  }
  
  resetNavItems(): void {
    this.navItemsSubject.next(this.defaultNavItems);
  }
}
